import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import fr from '../i18n/fr';
import ar from '../i18n/ar';
import { safeStorage } from '../utils/safeStorage';

type Language = 'fr' | 'ar';
type Translations = typeof fr;

interface LanguageContextType {
  language: Language;
  t: Translations;
  toggleLanguage: () => void;
  setLanguage: (lang: Language) => void;
  isRTL: boolean;
}

const translations: Record<Language, Translations> = { fr, ar: ar as unknown as Translations };

const LanguageContext = createContext<LanguageContextType>({
  language: 'fr',
  t: fr,
  toggleLanguage: () => {},
  setLanguage: () => {},
  isRTL: false,
});

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(
    () => (safeStorage.getItem('language') as Language) || 'fr'
  );

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    safeStorage.setItem('language', lang);
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
  }, []);

  const toggleLanguage = useCallback(() => {
    setLanguage(language === 'fr' ? 'ar' : 'fr');
  }, [language, setLanguage]);

  const value: LanguageContextType = {
    language,
    t: translations[language],
    toggleLanguage,
    setLanguage,
    isRTL: language === 'ar',
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

export default LanguageContext;
